import { useQuery } from "@tanstack/react-query";
import { api, type PTZPreset } from "../../lib/api";

/** How long a fetched preset list is reused before the device is asked again.
 *
 *  Presets change when somebody configures the camera, which is rare and
 *  never happens through this app. A minute is long enough that flipping
 *  through the filmstrip doesn't wake every dome on the site, and short
 *  enough that an operator who just stored a position on the camera sees it
 *  without wondering why it's missing. Deliberately not cached on the server:
 *  see the backend's PTZUsecase.ListPresets for why this app never keeps its
 *  own copy of the camera's data. */
const PRESETS_STALE_MS = 60_000;

export interface PtzPresets {
  presets: PTZPreset[];
  isLoading: boolean;
  /** True when the camera answered but has nothing stored — distinct from
   *  "we haven't asked yet", which is what lets the pad show a preset
   *  control only when there is something behind it. */
  isEmpty: boolean;
}

/** Reads the positions stored on a camera.
 *
 *  Only fetches when the viewer can actually drive the camera: the request
 *  leaves the cluster and wakes the device up, so making it for a viewer who
 *  would then be refused the recall is a round trip nobody benefits from.
 *
 *  A failure is deliberately not surfaced. The pad's directional controls
 *  work regardless, and a camera whose preset list can't be read is a camera
 *  with no preset buttons — not a broken pad plastered with an error the
 *  operator can do nothing about. The failure is still visible in the
 *  backend's logs, where somebody can act on it. */
export function usePtzPresets(cameraId: string, isEnabled: boolean): PtzPresets {
  const { data, isPending } = useQuery({
    queryKey: ["ptz-presets", cameraId],
    queryFn: () => api.ptz.listPresets(cameraId),
    enabled: isEnabled,
    staleTime: PRESETS_STALE_MS,
    // One attempt. A camera that did not answer is not likely to answer on a
    // retry a moment later, and the pad has nothing to wait for — the
    // directions already work.
    retry: false,
  });

  const presets = data ?? [];
  return {
    presets,
    isLoading: isEnabled && isPending,
    isEmpty: data !== undefined && presets.length === 0,
  };
}
